import { IOrganizationLanguage, IProviderLanguages } from "@auvious/asr";
import { ApiResource, PagedCollection } from "@auvious/common";
import {
  ApplicationProviderTypeEnum,
  TranscriptStateEnum,
  TranscriptTransformType,
} from "../../../core-ui.enums";
import {
  ApplicationService,
  AuviousRtcService,
  GenericErrorHandler,
} from "../../../services";
import {
  ITranscript,
  ITranscriptMessage,
  ITranscriptStatus,
  ITranscriptTransformStatus,
  ITranscriptTranslateStatus,
  ITransformContent,
} from "../../interfaces";
import {
  IProviderLanguage,
  ProviderOrganizationLanguage,
} from "../../ProviderLanguage";
import {
  AIModelUsage,
  IProviderModel,
  IProviderModelDefault,
  IProvisionLanguageOptions,
  IProvisionedModel,
  ITranscriptStrategy,
  ITranscriptTransformOptions,
  ITranscriptTransformStrategy,
  ITranscriptTranslateOptions,
  ITranscriptTranslateStrategy,
  LiveToolDefinition,
  SpeechLanguage,
} from "../ITranscriptStrategy";

export class ElevenLabsTranscriptStrategy
  implements
    ITranscriptStrategy,
    ITranscriptTransformStrategy,
    ITranscriptTranslateStrategy
{
  private resource: ApiResource;

  constructor(
    private logger: GenericErrorHandler,
    rtc: AuviousRtcService,
    private application: ApplicationService,
  ) {
    rtc.common$.subscribe((c) => {
      this.resource = c.apiResourceFactory("api/ai/");
    });
  }

  private get appID() {
    return this.application.getActiveApplication()?.getId();
  }

  private emptyTranscriptStatus(id = ""): ITranscriptStatus {
    return {
      id,
      state: TranscriptStateEnum.completed,
      language: "",
      createdAt: new Date(0),
    };
  }

  private emptyTranslationStatus(id = ""): ITranscriptTranslateStatus {
    return {
      ...this.emptyTranscriptStatus(id),
      transcript: "",
    };
  }

  private emptyTransformStatus(id = ""): ITranscriptTransformStatus {
    return {
      id,
      transcript: "",
      intent: "summary",
      state: TranscriptStateEnum.completed,
      createdAt: new Date(0),
    };
  }

  private emptyProviderModels(): {
    models: IProviderModel[];
    defaults: Record<AIModelUsage, IProviderModelDefault>;
    tools?: LiveToolDefinition[];
    regions: string[];
    voices: string[];
    speechLanguages?: SpeechLanguage[];
  } {
    return {
      models: [],
      defaults: {} as Record<AIModelUsage, IProviderModelDefault>,
      tools: [],
      regions: [],
      voices: [],
      speechLanguages: [],
    };
  }

  async createTranscriptRequest(
    conversationId: string,
    organizationLanguageId: string,
  ): Promise<{ conversationId: string; id: string }> {
    return { conversationId, id: "" };
  }

  async getTranscriptsForConversation(
    conversationId: string,
  ): Promise<ITranscriptStatus[]> {
    return [];
  }

  async getTranscriptStatusForConversation(
    conversationId: string,
    transcriptId: string,
  ): Promise<ITranscriptStatus> {
    return this.emptyTranscriptStatus(transcriptId);
  }

  async getTranscriptURL(
    conversationId: string,
    transcriptId: string,
    type: "inline" | "attachment",
  ): Promise<{ url: string; validUntil: string }> {
    return { url: "", validUntil: "" };
  }

  async getTranscript(
    conversationId: string,
    transcriptId: string,
  ): Promise<ITranscript> {
    return {
      chatTranscriptList: [],
      conversationId,
      instanceId: transcriptId,
      recorderId: "",
    };
  }

  async removeTranscript(conversationId: string, transcriptId: string) {}

  async getProvisionedLanguages(
    page: number,
    pageSize: number,
  ): Promise<PagedCollection<IOrganizationLanguage>> {
    return new PagedCollection<IOrganizationLanguage>({
      content: [],
      totalElements: 0,
      totalPages: 1,
      size: 0,
      number: 0,
    });
  }

  async provisionLanguage(
    language: IProviderLanguage,
    options: IProvisionLanguageOptions,
  ): Promise<{ organizationLanguageId: string }> {
    return { organizationLanguageId: "" };
  }

  async removeProvisionedLanguage(
    provider: ApplicationProviderTypeEnum,
    language: ProviderOrganizationLanguage,
  ) {}

  async getProviderLanguages(
    usage: "translations" | "transcriptions",
    page: number,
    pageSize: number,
  ): Promise<PagedCollection<IProviderLanguages>> {
    return new PagedCollection<IProviderLanguages>({
      content: [],
      totalElements: 0,
      totalPages: 1,
      size: 0,
      number: 0,
    });
  }

  async getProviderModels(
    providerType: ApplicationProviderTypeEnum,
    region?: string,
  ): Promise<{
    models: IProviderModel[];
    defaults: Record<AIModelUsage, IProviderModelDefault>;
    tools?: LiveToolDefinition[];
    regions: string[];
    voices: string[];
    speechLanguages?: SpeechLanguage[];
  }> {
    if (providerType !== ApplicationProviderTypeEnum.ELEVENLABS) {
      return this.emptyProviderModels();
    }
    try {
      const response = await this.resource.get({
        urlPostfix: [
          this.appID,
          "providers",
          ApplicationProviderTypeEnum.ELEVENLABS.toLowerCase(),
          "properties",
        ].join("/"),
        params: region ? { region } : undefined,
      });

      const models = ((response?.models as IProviderModel[]) || []).filter(
        (m) => m.provider === ApplicationProviderTypeEnum.ELEVENLABS,
      );

      return {
        models: models.filter((m) => m.agent || m.chat),
        defaults: response?.defaults || {},
        tools: (response?.tools as LiveToolDefinition[]) || [],
        regions: Array.isArray(response?.regions)
          ? (response.regions as string[])
          : [],
        voices: response?.voices || [],
        speechLanguages: response?.speechLanguages || [],
      };
    } catch (ex) {
      this.logger.handleNotAuthenticatedError(ex);
      throw ex;
    }
  }

  async getProvisionedModel(): Promise<IProvisionedModel[]> {
    try {
      const response = await this.resource.get({
        urlPostfix: [this.appID, "enabled/models"].join("/"),
      });
      return ((response?.models as IProvisionedModel[]) || []).filter(
        (m) =>
          m.provider === ApplicationProviderTypeEnum.ELEVENLABS &&
          (m.usage === "agent" || m.usage === "chat"),
      );
    } catch (ex) {
      if (ex.response?.status === 404) {
        return [];
      }
      this.logger.handleNotAuthenticatedError(ex);
      throw ex;
    }
  }

  async provisionModel(
    provider: ApplicationProviderTypeEnum,
    model: IProvisionedModel,
  ): Promise<{ organizationModelId: string }> {
    if (provider !== ApplicationProviderTypeEnum.ELEVENLABS) {
      return { organizationModelId: "" };
    }
    if (!(model?.usage === "agent" || model?.usage === "chat")) {
      return { organizationModelId: "" };
    }
    try {
      return await this.resource.create(
        { provider, id: model.id, usage: model.usage },
        { urlPostfix: [this.appID, "enabled/models"].join("/") },
      );
    } catch (ex) {
      this.logger.handleNotAuthenticatedError(ex);
      throw ex;
    }
  }

  async removeProvisionedModel(
    provider: ApplicationProviderTypeEnum,
    model: IProvisionedModel,
  ): Promise<void> {
    if (provider !== ApplicationProviderTypeEnum.ELEVENLABS) {
      return;
    }
    try {
      await this.resource.delete({
        urlPostfix: [
          this.appID,
          "enabled/models",
          encodeURIComponent(model.id),
          "providers",
          provider,
        ].join("/"),
      });
    } catch (ex) {
      this.logger.handleNotAuthenticatedError(ex);
      throw ex;
    }
  }

  async translate(
    conversationId: string,
    transcriptId: string,
    options: ITranscriptTranslateOptions,
  ): Promise<{ conversationId: string; id: string }> {
    return { conversationId, id: "" };
  }

  async getTranslations(
    conversationId: string,
    transcriptId: string,
  ): Promise<ITranscriptTranslateStatus[]> {
    return [];
  }

  async getTranslation(
    conversationId: string,
    transcriptId: string,
    translationId: string,
  ): Promise<ITranscriptTranslateStatus> {
    return this.emptyTranslationStatus(translationId);
  }

  async removeTranslation(
    conversationId: string,
    transcriptId: string,
    translationId: string,
  ): Promise<void> {}

  async getTranslationURL(
    conversationId: string,
    transcriptId: string,
    translationId: string,
  ): Promise<{ url: string; validUntil: string }> {
    return { url: "", validUntil: "" };
  }

  async getTranslationContent(
    conversationId: string,
    transcriptId: string,
    translationId: string,
  ): Promise<ITranscriptMessage[]> {
    return [];
  }

  isFeatureSupported(feature: TranscriptTransformType): boolean {
    return feature === "ai-assistant";
  }

  async transformTranscriptForConversation(
    conversationId: string,
    transcriptId: string,
    options: ITranscriptTransformOptions,
  ): Promise<{ conversationId: string; id: string }> {
    return { conversationId, id: "" };
  }

  async getTransformsForConversation(
    conversationId: string,
    transcriptId: string,
  ): Promise<ITranscriptTransformStatus[]> {
    return [];
  }

  async getTransformStatusForConversation(
    conversationId: string,
    transcriptId: string,
    transformId: string,
  ): Promise<ITranscriptTransformStatus> {
    return this.emptyTransformStatus(transformId);
  }

  async getTransformURL(
    conversationId: string,
    transcriptId: string,
    transfromId: string,
  ): Promise<{ url: string; validUntil: string }> {
    return { url: "", validUntil: "" };
  }

  async getTransformContent(
    conversationId: string,
    transcriptId: string,
    translationId: string,
  ): Promise<ITransformContent> {
    return { result: "" };
  }

  async removeTransform(
    conversationId: string,
    transcriptId: string,
    transformId: string,
  ): Promise<void> {}
}
